import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Menu, MenuItem, ListItemIcon, ListItemText, Divider } from "@mui/material";
import LogoFripick from "@/assets/logoFripick.svg";
import {
    Menu as MenuIcon,
    Person,
    Lock,
    ExitToApp,
    Apps,
    Fastfood,
    Domain,
    Settings,
    Visibility,
    PersonalVideo,
    FileCopy,
    Bolt,
    ArrowDropDown,
} from "@mui/icons-material";
import {
    StyledAppBar,
    StyledToolbar,
    LogoContainer,
    LogoImage,
    NavButton,
    RightSection,
    CuadreButton,
    ToggleMenuButton,
    MobileToggleMenuButton,
    NavigationBar,
    VerticalSeparator,
    ProfileButton,
} from "./header.styled";
import { AppContext } from "@/contexts/AppContext";
import useGetUserInfoQuery from "@/hooks/queries/useGetUserInfoQuery";
import { fripickBaseUrl, newProviderPlatformBaseUrl, providerPlatformBaseUrl } from "@/api/constants";
import PrintCashReport from "@/components/PrintReport/cashReport";
import Tooltip from "@mui/material/Tooltip";
import { clearSession } from "@/helper/auth";
const defaultAlert = {
    message: "",
    isOpen: false,
};

export default function Header() {
    const [userMenuAnchor, setUserMenuAnchor] = useState(null);
    const [showNavigation, setShowNavigation] = useState(false);
    const { data: userInfo } = useGetUserInfoQuery();
    const appContext = useContext(AppContext);
    const [isPrintOpen, setIsPrintOpen] = useState(false);
    const [displayAlert, setDisplayAlert] = useState(defaultAlert);
    const tooltipKey = useRef(null);
    const getUrl = (path) => {
        const params = new URLSearchParams({
            uid: userInfo?.data?.id,
            token: appContext.token,
            vendorId: userInfo?.data?.vendorId,
        });
        return `${providerPlatformBaseUrl}${path}?${params.toString()}`;
    };

    useEffect(() => {
        if (userInfo?.data && !userInfo?.data?.vendor) {
            alert("No tiene permiso para usar esta aplicación. Contacte al administrador de su cuenta.");
            handleLogout(true);
        }
    }, [userInfo?.data?.vendor]);
    const navigationItems = useMemo(() => {
      // If type is 6 (Cashier) only show the express confirmation item
      if (userInfo?.data?.userTypeId === 6) {
        return [];
      }
      return [
        { title: "Inicio", icon: Apps, url: getUrl("inicio") },
        { title: "Artículos", icon: Fastfood, url: getUrl("articulos") },
        {
          title: "Configuraciones Clientes",
          icon: Domain,
          url: getUrl("clientes"),
        },
        {
          title: "Mis Configuraciones",
          icon: Settings,
          url: getUrl("configuraciones"),
        },
        {
          title: "Visualización",
          icon: Visibility,
          url: getUrl("visualizacion"),
        },
        { title: "Reportes", icon: FileCopy, url: getUrl("reportes") }
      ];
    }, [userInfo?.data, appContext?.token]);
    const shouldShowNavigation = navigationItems.length > 0;
    const handleUserMenuOpen = (event) => {
        setUserMenuAnchor(event.currentTarget);
    };

    const handleUserMenuClose = () => {
        setUserMenuAnchor(null);
    };

    const handleToggleNavigation = () => {
        setShowNavigation(!showNavigation);
    };

    const handleLogout = (redirectToFripick = false) => {
        const hasCompanyId = !!sessionStorage.getItem("companyId");
        clearSession();
        appContext.authenticate({ token: undefined, userId: undefined, providerId: undefined, companyId: undefined });
        if (hasCompanyId || redirectToFripick) {
            window.open(fripickBaseUrl, "_self");
        } else {
            handleUserMenuClose();
        }
    };

    const handleDisplayAlert = ({ message }) => {
        setDisplayAlert({ message, isOpen: true });
        if (tooltipKey.current) {
            clearTimeout(tooltipKey.current);
        }
        tooltipKey.current = setTimeout(() => {
            setDisplayAlert(defaultAlert);
        }, 3000);
    };

    return (
        <>
            <PrintCashReport
                show={isPrintOpen}
                handleClose={() => setIsPrintOpen(false)}
                setAlert={handleDisplayAlert}
            />
            <StyledAppBar position="static">
                <StyledToolbar>
                    <LogoContainer>
                        <MobileToggleMenuButton onClick={handleToggleNavigation}>
                            <MenuIcon style={{ fontSize: 25, marginRight: 4 }} />
                        </MobileToggleMenuButton>
                        <LogoImage src={LogoFripick} className="logo custom" alt="Custom logo" />
                        <VerticalSeparator />
                        {shouldShowNavigation && (
                        <ToggleMenuButton onClick={handleToggleNavigation}>
                            <MenuIcon style={{ fontSize: 20, marginRight: 4 }} />
                            {showNavigation ? "Ocultar Menú" : "Mostrar Menú"}
                        </ToggleMenuButton>
                        )}
                    </LogoContainer>

                    <RightSection>
                        <Tooltip
                            title={displayAlert.message}
                            open={displayAlert.isOpen}
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        backgroundColor: "#d32f2f",
                                        color: "#ffff",
                                        fontSize: "15px",
                                    },
                                },
                                arrow: {
                                    sx: {
                                        color: "#d32f2f",
                                    },
                                },
                            }}
                            arrow
                        >
                            <CuadreButton variant="contained" onClick={() => setIsPrintOpen(true)}>
                                Cuadre Diario
                            </CuadreButton>
                        </Tooltip>

                        <ProfileButton onClick={handleUserMenuOpen} style={{ color: "#666" }}>
                            <Person />
                            <h6>{userInfo?.data?.name}</h6>
                            <ArrowDropDown
                                style={{
                                    marginTop: "-4px",
                                }}
                            />
                        </ProfileButton>
                    </RightSection>
                </StyledToolbar>

              {shouldShowNavigation && (
                <NavigationBar show={showNavigation}>
                    {navigationItems.map((item, index) => (
                        <NavButton key={index} variant="text" onClick={() => window.open(item.url, "_self")}>
                            {<item.icon fontSize="small" />}
                            {item.title}
                        </NavButton>
                    ))}
                </NavigationBar>
              )}

                <Menu
                    anchorEl={userMenuAnchor}
                    open={Boolean(userMenuAnchor)}
                    onClose={handleUserMenuClose}
                    transformOrigin={{ horizontal: "right", vertical: "top" }}
                    anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
                >
                    <MenuItem onClick={() => window.open(getUrl("configuraciones/Perfil/"), "_self")}>
                        <ListItemIcon>
                            <Person fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Editar Perfil" />
                    </MenuItem>
                    <MenuItem onClick={() => window.open(getUrl("configuraciones/Perfil/"), "_self")}>
                        <ListItemIcon>
                            <Lock fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Cambiar Contraseña" />
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={() => handleLogout()}>
                        <ListItemIcon>
                            <ExitToApp fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary="Salir" />
                    </MenuItem>
                </Menu>
            </StyledAppBar>
        </>
    );
}
